import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import {
  Building2, LayoutDashboard, Users, Briefcase, ClipboardList,
  Calendar, FileText, Settings, LogOut, ChevronLeft, ChevronRight,
  FolderKanban, Clock, Target, Sparkles, Activity, CalendarDays, Menu, X,
  Repeat, Timer
} from 'lucide-react';

const ADMIN_MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/crm/dashboard' },
  { id: 'clients', label: 'Clients', icon: Briefcase, path: '/crm/clients' },
  { id: 'employees', label: 'Team', icon: Users, path: '/crm/team' },
  { id: 'tasks', label: 'Tasks', icon: ClipboardList, path: '/crm/tasks' },
  { id: 'recurring', label: 'Recurring', icon: Repeat, path: '/crm/recurring' },
  { id: 'work-logs', label: 'Work Logs', icon: Clock, path: '/crm/work-logs' },
  { id: 'time', label: 'Time Track', icon: Timer, path: '/crm/time' },
  { id: 'work-library', label: 'Work Library', icon: FolderKanban, path: '/crm/work-library' },
  { id: 'leave', label: 'Leave', icon: CalendarDays, path: '/crm/leave' },
  { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/crm/calendar' },
  { id: 'activity', label: 'Activity', icon: Activity, path: '/crm/activity' },
  { id: 'reports', label: 'Reports', icon: FileText, path: '/crm/reports' },
  { id: 'kpi', label: 'KPI', icon: Target, path: '/crm/kpi' },
  { id: 'ai-insights', label: 'AI Insights', icon: Sparkles, path: '/crm/ai-insights', badge: 'AI' },
  { id: 'settings', label: 'Settings', icon: Settings, path: '/crm/settings' },
];

const EMPLOYEE_MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/crm/dashboard' },
  { id: 'my-tasks', label: 'My Tasks', icon: ClipboardList, path: '/crm/tasks' },
  { id: 'my-clients', label: 'My Clients', icon: Briefcase, path: '/crm/clients' },
  { id: 'work-logs', label: 'Work Logs', icon: Clock, path: '/crm/work-logs' },
  { id: 'time', label: 'Time Track', icon: Timer, path: '/crm/time' },
  { id: 'leave', label: 'Leave', icon: CalendarDays, path: '/crm/leave' },
  { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/crm/calendar' },
];

export default function CRMLayout({ children }) {
  const { user, logout, isAdmin, loading } = useCRMAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/crm/login');
    }
  }, [loading, user, navigate]);

  const menu = isAdmin() ? ADMIN_MENU : EMPLOYEE_MENU;

  const handleLogout = () => {
    logout();
    navigate('/crm/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Don't render anything while redirecting
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-slate-900 text-white flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} className="text-white">
            <Menu className="w-6 h-6" />
          </Button>
          <span className="font-bold">Agency CRM</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-sm font-bold">
            {user?.name?.charAt(0) || 'U'}
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 h-screen bg-slate-900 text-white transition-all duration-300 z-50 flex flex-col",
        // Desktop
        "hidden lg:flex",
        collapsed ? "lg:w-20" : "lg:w-64",
        // Mobile
        sidebarOpen && "!flex w-72"
      )}>
        {/* Logo */}
        <div className="h-14 lg:h-16 flex items-center justify-between px-4 border-b border-white/10">
          <Link to="/crm/dashboard" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            {(!collapsed || sidebarOpen) && <span className="font-bold text-lg">Agency CRM</span>}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => sidebarOpen ? setSidebarOpen(false) : setCollapsed(!collapsed)}
            className="text-slate-400 hover:text-white hover:bg-white/10"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {menu.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all",
                  isActive ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                )}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {(!collapsed || sidebarOpen) && (
                  <>
                    <span className="flex-1 text-sm">{item.label}</span>
                    {item.badge && <Badge className="bg-indigo-400/20 text-indigo-300 text-xs">{item.badge}</Badge>}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-white/10">
          <div className={cn("flex items-center gap-3 px-3 py-2", collapsed && !sidebarOpen && "justify-center")}>
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
              {user?.name?.charAt(0) || 'U'}
            </div>
            {(!collapsed || sidebarOpen) && (
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role?.replace('_', ' ')}</p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={cn(
              "w-full mt-2 text-slate-400 hover:text-white hover:bg-white/10",
              collapsed && !sidebarOpen && "px-0 justify-center"
            )}
          >
            <LogOut className="w-5 h-5" />
            {(!collapsed || sidebarOpen) && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "transition-all duration-300 min-h-screen",
        "pt-14 lg:pt-0", // Mobile header space
        collapsed ? "lg:ml-20" : "lg:ml-64"
      )}>
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
